import { useMemo } from 'react';

import { observer, useLocalObservable } from 'mobx-react-lite';

import { Button } from '@components/button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { DataTable } from 'components/DataTable';
import { configs } from 'jobboards/configs';
import { useStore } from 'models/Provider';

import { JobBoardLink } from './JobBoardLink';
import { TableWrap } from './TableWrap';

export const ActiveJobBoards = observer(({ boards, deactivate }) => {
  if (boards.length === 0) {
    return <NoConnections />;
  }
  return <ConnectedBoards boards={boards} deactivate={deactivate} />;
});

const RenderPrice = observer(({ item }) => {
  const { lang } = useStore();
  const config = configs[item.name];
  const texts = config[lang];
  return <span>{texts.priceText}</span>;
});

const ConnectedBoards = observer(({ boards, deactivate }) => {
  const { t } = useStore();
  const columns = useMemo(() => {
    return [
      {
        title: t('jobboards.table.jobboard'),
        render: (item) => <div>{configs[item.name].title}</div>,
      },
      { title: t('jobboards.table.price'), render: (item) => <RenderPrice item={item} /> },
      {
        title: t('jobboards.table.link'),
        render: (item) => <JobBoardLink link={configs[item.name].link} />,
      },
      {
        width: 0.5,
        style: { display: 'flex', justifyContent: 'flex-end' },
        render: (item) => (
          <RenderDisconnectButton key={item.name} item={item} deactivate={deactivate} />
        ),
      },
    ];
  }, [t, deactivate]);

  return (
    <Stack space={3}>
      <Text font="bold18" text={t('jobboards.settings.connected')} />
      <TableWrap>
        <DataTable
          spacing={false}
          columns={columns}
          data={boards}
          rowStyle={{ background: 'transparent' }}
        />
      </TableWrap>
    </Stack>
  );
});

const NoConnections = () => {
  const { t } = useStore();

  return (
    <Stack space={2}>
      <Text font="bold18" text={t('jobboards.settings.noc')} />
      <Text font="regular14" color="textSecondary" text={t('jobboards.settings.nocText')} />
    </Stack>
  );
};

const RenderDisconnectButton = observer(({ item, deactivate }) => {
  const { t } = useStore();
  const state = useLocalObservable(() => ({
    submitting: false,
    setSubmitting(val) {
      state.submitting = val;
    },
    async submit() {
      state.setSubmitting(true);
      const success = await deactivate(item.name);
      if (!success) {
        state.setSubmitting(false);
      }
    },
  }));

  return (
    <>
      <Button
        variant="secondary"
        isDisabled={state.submitting}
        onClick={state.submit}
        text={t('jobboards.settings.btn.disconnect')}
      />
    </>
  );
});
